"use client";

import React, { useState } from "react";
import { Tag, Folder, Hammer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Link } from "@/components/common/Link";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import {
  EcommerceProductsProvider,
  useEcommerceProducts,
  AddToCartButton,
} from "@/integrations/wordpress/WordPressEcommerceProductsProvider";

declare global {
  interface Window {
    wvcClient: any;
  }
}

function SingleProductConsumer() {
  const { products, loading, error } = useEcommerceProducts();
  const [quantity, setQuantity] = useState(1);

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div className="aspect-square bg-muted/30 animate-pulse" />
        <div className="space-y-6">
          <div className="h-12 bg-muted/30 animate-pulse w-3/4" />
          <div className="h-6 bg-muted/30 animate-pulse w-1/3" />
          <div className="h-24 bg-muted/30 animate-pulse" />
          <div className="h-12 bg-muted/30 animate-pulse" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-20">
        <p className="text-muted-foreground">Unable to load product details.</p>
      </div>
    );
  }

  if (!products || products.length === 0) {
    return (
      <div className="text-center py-20">
        <p className="text-muted-foreground">Product not found.</p>
      </div>
    );
  }

  const product = products[0];
  const hasDiscount = product.discounted_price !== undefined && product.discounted_price !== null && product.discounted_price < product.price;

  return (
    <div className="space-y-20">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
        {}
        <div className="relative">
          <div
            className="relative aspect-square overflow-hidden border border-border"
            style={{
              background: "linear-gradient(135deg, hsl(210 100% 50% / 0.1), hsl(240 10% 4% / 0.05))",
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)",
              boxShadow: "var(--shadow-lg)",
            }}
          >
            {product.featured_image_url ? (
              <img
                src={product.featured_image_url}
                alt={product.title}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-muted">
                <Hammer className="size-20 text-muted-foreground" />
              </div>
            )}
            {hasDiscount && (
              <div className="absolute top-6 left-6">
                <Badge className="bg-primary text-primary-foreground uppercase tracking-widest text-xs px-3 py-1.5 rounded-lg">
                  Sale
                </Badge>
              </div>
            )}
          </div>
        </div>

        {}
        <div className="flex flex-col justify-center space-y-8">
          <div className="space-y-4">
            {product.categories && product.categories.length > 0 && (
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-primary font-default font-semibold">
                <span className="inline-block w-8 h-px bg-primary" />
                {product.categories[0].name}
              </div>
            )}
            <h1 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground">
              {product.title}
            </h1>
          </div>

          {}
          <div className="flex items-baseline gap-4 border-y border-border py-6">
            {hasDiscount ? (
              <>
                <span className="font-default text-4xl font-bold text-primary">
                  ${product.discounted_price?.toFixed(2)}
                </span>
                <span className="font-default text-2xl text-muted-foreground line-through">
                  ${product.price.toFixed(2)}
                </span>
              </>
            ) : (
              <span className="font-default text-4xl font-bold text-foreground">
                ${product.price?.toFixed(2)}
              </span>
            )}
          </div>

          {}
          {product.short_description && (
            <div
              className="font-default text-lg leading-relaxed text-muted-foreground [&_p]:mb-0"
              dangerouslySetInnerHTML={{ __html: product.short_description }}
            />
          )}

          {}
          <div className="space-y-4 pt-2">
            <div className="flex items-center gap-4">
              <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-default">
                Quantity
              </span>
              <div className="flex items-center border border-border rounded-lg">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-11 w-11 text-foreground hover:bg-accent hover:text-primary"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  aria-label="Decrease quantity"
                >
                  −
                </Button>
                <span className="w-14 text-center font-default font-semibold text-foreground">
                  {quantity}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-11 w-11 text-foreground hover:bg-accent hover:text-primary"
                  onClick={() => setQuantity(quantity + 1)}
                  aria-label="Increase quantity"
                >
                  +
                </Button>
              </div>
            </div>

            <AddToCartButton
              productId={product.id}
              variant="default"
              size="lg"
              className="w-full"
              buttonClassName="bg-primary text-primary-foreground w-full h-14 text-sm uppercase tracking-[0.2em] font-bold hover:bg-primary/90 rounded-lg"
              text="Buy Now"
              showQuantity={false}
            />
          </div>

          {}
          <div className="space-y-3 pt-4 border-t border-border">
            {product.categories && product.categories.length > 0 && (
              <div className="flex items-start gap-3 text-sm">
                <Folder className="size-4 text-primary mt-0.5 shrink-0" />
                <div className="flex flex-wrap gap-2">
                  <span className="text-muted-foreground uppercase tracking-wider text-xs font-default">Categories:</span>
                  {product.categories.map((cat, i) => (
                    <Link
                      key={cat.id}
                      data-index={i}
                      to={cat.link || "#"}
                      className="text-foreground hover:text-primary transition-colors font-default"
                    >
                      {cat.name}
                      {i < product.categories.length - 1 && ","}
                    </Link>
                  ))}
                </div>
              </div>
            )}
            {product.tags && product.tags.length > 0 && (
              <div className="flex items-start gap-3 text-sm">
                <Tag className="size-4 text-primary mt-0.5 shrink-0" />
                <div className="flex flex-wrap gap-2">
                  <span className="text-muted-foreground uppercase tracking-wider text-xs font-default">Tags:</span>
                  {product.tags.map((tag, i) => (
                    <Link
                      key={tag.id}
                      data-index={i}
                      to={tag.link || "#"}
                      className="text-foreground hover:text-primary transition-colors font-default"
                    >
                      {tag.name}
                      {i < product.tags.length - 1 && ","}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {}
      {product.long_description && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 pt-12 border-t border-border">
          <div className="lg:col-span-4">
            <div className="sticky top-24 space-y-3">
              <div className="text-xs uppercase tracking-[0.2em] text-primary flex items-center gap-2 font-default font-semibold">
                <span className="inline-block w-8 h-px bg-primary" />
                Details
              </div>
              <h2 className="font-default text-3xl md:text-4xl font-bold tracking-tight text-foreground">
                Product Specifications
              </h2>
            </div>
          </div>
          <div className="lg:col-span-8">
            <div
              className="font-default text-lg leading-relaxed text-foreground/90 space-y-4 [&_p]:mb-4 [&_p:last-child]:mb-0 [&_h2]:font-default [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:mt-8 [&_h2]:mb-3 [&_h3]:font-default [&_h3]:text-xl [&_h3]:font-bold [&_h3]:mt-6 [&_h3]:mb-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:space-y-2"
              dangerouslySetInnerHTML={{ __html: product.long_description }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function RelatedProductsConsumer() {
  const { products, loading } = useEcommerceProducts();
  const relatedProducts = products.length > 1 ? products.slice(1) : products;

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[...Array(4)].map((_, i) => (
          <div key={i} data-index={i} className="aspect-square bg-muted/30 animate-pulse rounded-lg" />
        ))}
      </div>
    );
  }

  if (!relatedProducts || relatedProducts.length === 0) {
    return (
      <p className="text-muted-foreground text-center py-8 font-default">No related devices available.</p>
    );
  }

  return (
    <Carousel opts={{ align: "start", loop: false }} className="w-full">
      <CarouselContent className="-ml-4">
        {relatedProducts.map((product, i) => (
          <CarouselItem
            key={product.id}
            data-index={i}
            className="pl-4 basis-full sm:basis-1/2 lg:basis-1/3 xl:basis-1/4"
          >
            <article
              className="group h-full flex flex-col border border-border overflow-hidden transition-all duration-300 hover:border-primary rounded-lg"
              style={{
                background: "hsl(0 0% 100%)",
                boxShadow: "var(--shadow-md)",
              }}
            >
              <Link to={product.link || "#"} className="block">
                <div className="aspect-square overflow-hidden bg-muted">
                  {product.featured_image_url ? (
                    <img
                      src={product.featured_image_url}
                      alt={product.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Hammer className="size-12 text-muted-foreground" />
                    </div>
                  )}
                </div>
              </Link>
              <div className="p-5 flex flex-col flex-1 gap-3">
                {product.categories && product.categories.length > 0 && (
                  <div className="text-[10px] uppercase tracking-[0.2em] text-primary font-default font-bold">
                    {product.categories[0].name}
                  </div>
                )}
                <Link to={product.link || "#"} className="block">
                  <h3 className="font-default text-lg font-bold leading-tight text-foreground group-hover:text-primary transition-colors line-clamp-2">
                    {product.title}
                  </h3>
                </Link>
                <div className="font-default text-base font-semibold text-foreground mt-auto">
                  ${product.price?.toFixed(2)}
                </div>
                <AddToCartButton
                  productId={product.id}
                  variant="outline"
                  size="sm"
                  className="w-full"
                  buttonClassName="bg-primary text-primary-foreground w-full uppercase tracking-widest text-xs font-bold border-0 hover:bg-primary/90 rounded-lg"
                  text="Buy Now"
                  showQuantity={false}
                />
              </div>
            </article>
          </CarouselItem>
        ))}
      </CarouselContent>
      <div className="hidden md:block">
        <CarouselPrevious className="left-0 -translate-x-1/2 bg-background border-border text-foreground hover:bg-primary hover:text-primary-foreground rounded-lg" />
        <CarouselNext className="right-0 translate-x-1/2 bg-background border-border text-foreground hover:bg-primary hover:text-primary-foreground rounded-lg" />
      </div>
    </Carousel>
  );
}

export default function SingleProductTemplateSection() {
  const currentWPQueryParams =
    typeof window !== "undefined" && window.wvcClient
      ? window.wvcClient.getCurrentWPQueryParams()
      : {};
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section data-section-id="4239"
      id="single-product-template-section"
      className="bg-background text-foreground py-16 md:py-24 px-4 md:px-8 relative overflow-hidden"
    >
      {}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(var(--foreground) 1px, transparent 1px), linear-gradient(90deg, var(--foreground) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />

      <div className="relative max-w-7xl mx-auto">
        {}
        <div className="mb-12 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground font-default">
          <Link to="/HomePage" className="hover:text-primary transition-colors text-foreground">
            Home
          </Link>
          <span>/</span>
          <Link to="/ShopPage" className="hover:text-primary transition-colors text-foreground">
            Shop
          </Link>
          <span>/</span>
          <span className="text-primary">Device Details</span>
        </div>

        {}
        <EcommerceProductsProvider wp_query={wp_query}>
          <SingleProductConsumer />

          {}
          <div className="mt-24 md:mt-32 pt-16 border-t border-border">
            <div className="mb-12 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
              <div className="space-y-3 max-w-2xl">
                <div className="text-xs uppercase tracking-[0.2em] text-primary flex items-center gap-2 font-default font-semibold">
                  <span className="inline-block w-8 h-px bg-primary" />
                  Explore More
                </div>
                <h2 className="font-default text-4xl md:text-5xl font-bold tracking-tight text-foreground">
                  Recommended Devices
                </h2>
                <p className="font-default text-lg text-muted-foreground">
                  Browse our latest collection of smartphones and accessories at TechWire Mobile.
                </p>
              </div>
              <Link to="/ShopPage">
                <Button
                  variant="outline"
                  className="border-border text-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary uppercase tracking-[0.2em] text-xs font-bold h-12 px-6 rounded-lg"
                >
                  View All Products
                </Button>
              </Link>
            </div>

            <RelatedProductsConsumer />
          </div>
        </EcommerceProductsProvider>
      </div>
    </section>
  );
}